import React from "react"
import { combineReducers } from "redux"
import * as ActionTypes from "@/stores/sale/actions"
import _ from "lodash"
import { DataType } from "@/pages/workbench/data/common"
import {  CLEANING_STATUS } from "@/constants/Constants"

const loginStatusState = {
  oneOf: () => false,
  allOf: () => false,
}

/**
 * 登录状态
 * @param state
 * @param action
 * @returns {*}
 */
function loginStatus(state = loginStatusState, action) {
  switch (action.type) {
    case ActionTypes.FETCH_LOGIN_STATUS.SUCCESS: {
      const { userPermissions = [] } = action.data
      const permissions = [...userPermissions]

      return {
        ...action.data,
        /**
         * @description 至少有一个权限即可通过
         * @param perm {number | array}
         * @returns {boolean}
         */
        oneOf: (perm) => {
          if (typeof perm === "number") {
            return _.includes(permissions, perm)
          }
          return _.intersection(perm, permissions).length > 0
        },
        /**
         * @description 所有的权限需都满足才可通过
         * @param perm {number | array}
         * @returns {boolean}
         */
        allOf: (perm) => {
          if (typeof perm === "number") {
            return _.includes(permissions, perm)
          }
          return _.difference(perm, permissions).length === 0
        },
      }
    }

    case ActionTypes.SWITCH_AGENCY.SUCCESS:
      return {
        ...state,
        agencyId: action.data.agencyId,
      }

    default:
      return state
  }
}

/**
 * 用户信息
 * @param state
 * @param action
 * @return {{}}
 */
function userRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_USER.QUERY: {
      const userId = action.args[0]
      return {
        ...state,
        [userId]: _.assign({ query: true }, state[userId]),
      }
    }

    case ActionTypes.FETCH_USER.SUCCESS:
      return {
        ...state,
        [action.data.userId]: action.data,
      }

    case ActionTypes.FETCH_EMPLOYEE_USER_LIST.SUCCESS:
      return {
        ...state,
        ..._.zipObject(
          _.map(action.data.list, (employee) => employee.employeeId),
          _.map(action.data.list, (employee) => ({
            userId: employee.employeeId,
            displayName: employee.operationUserName,
            entryTime: employee.entryTime,
          }))
        ),
      }

    default:
      return state
  }
}

/**
 * 当前用户信息
 */
function currentUser(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_CURRENT_USER.SUCCESS:
      return action.data

    case ActionTypes.REFRESH_CURRENT_USER:
      return action.data

    default:
      return state
  }
}

/**
 * 代理商首选项
 * @param state
 * @param action
 * @returns {*}
 */
// https://e.qq.com/ags/preference/agency/id
function agencyPreferenceRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_AGENCY_PREFERENCE.SUCCESS:
      return {
        ...state,
        [action.data.agencyId]: action.data,
      }

    case ActionTypes.REFRESH_AGENCY_PREFERENCE:
      return _.assign({}, state, { [action.data.agencyId]: action.data })

    default:
      return state
  }
}

function agencyRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_AGENCY.SUCCESS: {
      const { agencyId } = action.data
      return {
        ...state,
        [agencyId]: _.merge(state[agencyId], action.data, { callCenterList: _.filter(action.data.callCenterList, (c) => !c.deleted) }),
      }
    }

    case ActionTypes.FETCH_AGENCY_LIST.SUCCESS: {
      const list = _.map(action.data.items, (agency) => _.merge(agency, { callCenterList: _.filter(agency.callCenterList, (c) => !c.deleted) }))
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(list, (agency) => agency.agencyId),
          list
        )
      )
    }

    case ActionTypes.REFRESH_AGENCY: {
      let { agencyId, employeeAmountIncrease, employeeAmount, seatAmount, seatAmountIncrease } = action.data
      const employeeAmountOld = (state[agencyId] || {}).employeeAmount
      if (!employeeAmount && employeeAmountIncrease) {
        employeeAmount = !!employeeAmountOld ? employeeAmountOld + employeeAmountIncrease : employeeAmountIncrease
        delete action.data.employeeAmountIncrease
      }
      const callCenter = _.get(state[agencyId], "callCenterList.0")
      if (callCenter && !seatAmount && seatAmountIncrease) {
        callCenter.seatAmount = !!callCenter.seatAmount ? callCenter.seatAmount + seatAmountIncrease : seatAmountIncrease
        delete action.data.seatAmountIncrease
      }
      return _.assign({}, state, {
        [agencyId]: _.merge(state[agencyId], action.data, {
          employeeAmount,
          callCenterList: [callCenter],
        }),
      })
    }

    case ActionTypes.FETCH_SEAT_INFO.SUCCESS: {
      const agencies = _.assign({}, state)
      const seatMap = _.zipObject(
        _.map(action.data, (s) => s.externalId),
        action.data
      )
      _.forEach(agencies, (agency) => {
        _.forEach(agency.callCenterList, (c) => {
          c.seatAmount = _.get(seatMap, `${c.externalId}.seatAmount`) || c.seatAmount
        })
      })
      return agencies
    }

    default:
      return state
  }
}

function agencyList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_AGENCY_LIST.SUCCESS:
      return {
        items: _.map(action.data.items, (agency) => agency.agencyId),
      }

    default:
      return state
  }
}

function agencyListPage(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_AGENCY_LIST_PAGE.SUCCESS:
      return {
        ...action.data,
      }

    default:
      return state
  }
}

function channelManagerRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_CHANNEL_MANAGER_LIST.SUCCESS: {
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.list, (manager) => manager.managerId),
          action.data.list
        )
      )
    }

    case ActionTypes.REFRESH_CHANNEL_MANAGER: {
      const { managerId } = action.data
      return _.assign({}, state, {
        [managerId]: _.assign({}, state[managerId], action.data),
      })
    }

    default:
      return state
  }
}

/**  渠道经理列表
 {
   query: true/false,
   status: 'SUCCESS'/'QUERY'/'ERROR',
   list: [id1, id2, xxx],
   totalCount: xx,
   totalPages: xxx
}
 */
function channelManagerList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_CHANNEL_MANAGER_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_CHANNEL_MANAGER_LIST.SUCCESS:
      return _.assign(action.data, {
        status: "SUCCESS",
        query: false,
        list: _.map(action.data.list, (manager) => manager.managerId),
      })

    case ActionTypes.FETCH_CHANNEL_MANAGER_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

/**  架构树
 {
  tree: {companyId, companyName, teams, freeEmployees}
}
 */
function organizationTree(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_ORGANIZATION_TREE.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_ORGANIZATION_TREE.SUCCESS:
      return _.assign({}, state, { status: "SUCCESS", query: false, tree: action.data })

    case ActionTypes.FETCH_ORGANIZATION_TREE.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    case ActionTypes.REFRESH_EMPLOYEE: {
      const { employeeId, operationUserName } = action.data
      if (!employeeId || !operationUserName) {
        return state
      }
      const updateEmployeeName = (employees) => {
        // 更新雇员名称
        _.forEach(employees, (employee) => {
          if (employee.userId === employeeId) {
            employee.name = operationUserName
          }
        })
      }
      const updateName = (teams) => {
        _.forEach(teams, (team) => {
          // 更新雇员名称
          updateEmployeeName(team.employees)
          // 更新子团队中的雇员名称
          if (_.isEmpty(team.children)) {
            updateName(team.children)
          }
        })
      }
      if (state.tree) {
        updateName(state.tree.teams)
        updateEmployeeName(state.tree.freeEmployees)
      }
      return _.assign({}, state)
    }

    // 指定日期段的树
    case ActionTypes.FETCH_ORGANIZATION_TREE_WITH_DATE_RANGE.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_ORGANIZATION_TREE_WITH_DATE_RANGE.SUCCESS:
      return _.assign({}, state, { status: "SUCCESS", query: false, treeWithDateRange: action.data })

    case ActionTypes.FETCH_ORGANIZATION_TREE_WITH_DATE_RANGE.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

/**  团队信息
 {
  [teamId]: {...}
}
 */
function teamRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_ORGANIZATION_TREE.SUCCESS: {
      const zipTeam = (teams) => {
        if (!teams || _.isEmpty(teams)) {
          return {}
        }
        return _.assign(
          _.zipObject(
            _.map(teams, (t) => t.teamId),
            _.map(teams, (t) => _.defaults({ children: null, employees: _.map(t.employees, (e) => e.userId) }, t))
          ),
          zipTeam(_.compact(_.flatten(_.map(teams, (t) => t.children))))
        )
      }
      return zipTeam(action.data.teams)
    }

    default:
      return state
  }
}

/** 用户信息
 {
  [employeeId1]: {...}
}
 */
function employeeRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.SWITCH_AGENCY.SUCCESS:
      return {}

    case ActionTypes.FETCH_EMPLOYEE_USER_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.list, (user) => user.employeeId),
          action.data.list
        )
      )

    case ActionTypes.FETCH_EMPLOYEE_USER.SUCCESS:
    case ActionTypes.FETCH_EMPLOYEE_SEAT_INFO.SUCCESS:
    case ActionTypes.REFRESH_EMPLOYEE: {
      const { employeeId } = action.data
      return _.assign({}, state, { [employeeId]: _.merge(state[employeeId], action.data) })
    }

    case ActionTypes.FETCH_EMPLOYEE_BOUND_ROLES.SUCCESS: {
      const { employeeId } = action.params
      return _.assign({}, state, { [employeeId]: _.assign({}, state[employeeId], { roleIds: _.map(action.data, (r) => r.roleId) }) })
    }

    case ActionTypes.FETCH_MANAGED_BM_OPERATORS.SUCCESS: {
      console.debug(action.params)
      console.debug(action.data)
      const { employeeId } = action.params
      return _.assign({}, state, { [employeeId]: _.assign({}, state[employeeId], { managedBmOperators: action.data }) })
    }

    case ActionTypes.FETCH_MANAGED_SUB_ADVERTISERS.SUCCESS: {
      const { employeeId } = action.params
      return _.assign({}, state, { [employeeId]: _.assign({}, state[employeeId], { managedSubAdvertisers: action.data }) })
    }

    case ActionTypes.REFRESH_EMPLOYEE_PRIVILEGE: {
      const { employeeId, permissionIds, privilegeIdList, permissions, activateState } = action.data
      if (permissions) {
        return _.assign({}, state, {
          [employeeId]: _.assign({}, state[employeeId], {
            privilegeIdList: _.map(permissions, (p) => p.permissionId),
            permissions,
            status: activateState && activateState.status,
            validPeriodStartTime: activateState && activateState.beginDate,
            validPeriodEndTime: activateState && activateState.endDate,
          }),
        })
      }
      if (permissionIds || privilegeIdList) {
        return _.assign({}, state, { [employeeId]: _.assign({}, state[employeeId], { privilegeIdList: permissionIds || privilegeIdList }) })
      }
      return state
    }

    default:
      return state
  }
}

/** 所有用户信息
 {
  [employeeId1]: {...}
}
 */
function fullEmployeeRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.SWITCH_AGENCY.SUCCESS:
      return {}

    case ActionTypes.FETCH_FULL_EMPLOYEE_USER_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.list, (user) => user.employeeId),
          action.data.list
        )
      )

    default:
      return state
  }
}

function fullEmployeeList(state = { selectedEmployeeList: [] }, action) {
  switch (action.type) {
    case ActionTypes.SWITCH_AGENCY.SUCCESS:
      return {}

    case ActionTypes.FETCH_FULL_EMPLOYEE_USER_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_FULL_EMPLOYEE_USER_LIST.SUCCESS:
      return _.assign({}, state, {
        status: "SUCCESS",
        query: false,
        list: _.map(action.data.list, (user) => user.employeeId),
        totalCount: action.data.totalCount,
      })

    case ActionTypes.FETCH_FULL_EMPLOYEE_USER_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

/** 用户列表
 {
  query, error, status， list
}
 */
function employeeList(state = { selectedEmployeeList: [] }, action) {
  switch (action.type) {
    case ActionTypes.SWITCH_AGENCY.SUCCESS:
      return {}

    case ActionTypes.FETCH_EMPLOYEE_USER_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_EMPLOYEE_USER_LIST.SUCCESS:
      return _.assign({}, state, {
        status: "SUCCESS",
        query: false,
        list: _.map(action.data.list, (user) => user.employeeId),
        totalCount: action.data.totalCount,
      })

    case ActionTypes.FETCH_EMPLOYEE_USER_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    case ActionTypes.SET_SELECTED_EMPLOYEE_LIST:
      return _.assign({}, state, { selectedEmployeeList: action.data })
    default:
      return state
  }
}

function industryRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_INDUSTRY_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data, (industry) => industry.industryId),
          action.data
        )
      )

    default:
      return state
  }
}

function industryList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_INDUSTRY_LIST.SUCCESS:
      return {
        items: _.chain(action.data)
          .filter((industry) => industry.status !== "hidden" && industry.status !== "disabled")
          .sortBy((industry) => industry.status)
          .map((industry) => industry.industryId)
          .value(),
        level2Items: _.chain(action.data)
          .filter((industry) => industry.status !== "hidden" && industry.status !== "disabled" && industry.level === 2)
          .sortBy((industry) => industry.status)
          .map((industry) => industry.industryId)
          .value(),
      }

    default:
      return state
  }
}

function industryCreate(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_INDUSTRY_LIST.SUCCESS:
      const firstLevel = []
      const secondLevel = []
      const statusNameMap = {
        active: '',
        pre_deprecated: '（预废弃）',
        deprecated: '（废弃）',
      };
      action.data
        .filter((item) => (item.status !== "hidden" && item.status !== "disabled"))
        .forEach((item) => {
          const name = `${item.displayName}${statusNameMap[item.status] || ''}`;
          if (item.level === 1) {
            firstLevel.push({
              id: item.industryId,
              name,
              isdisabled: item.status,
              children: [],
              definition: item.definition,
            })
          } else if (item.level === 2) {
            secondLevel.push({
              id: item.industryId,
              name,
              parentId: item.parentId,
              isdisabled: item.status,
              definition: item.definition,
            })
          }
        })

      firstLevel.forEach((fItem) => {
        secondLevel.forEach((sItem) => {
          if (fItem.id === sItem.parentId) {
            fItem.children.push(sItem)
          }
        })
      })

      return firstLevel
    default:
      return state
  }
}

function promotedObjectRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_PROMOTED_OBJECT_LIST.SUCCESS:
      return action.data

    default:
      return state
  }
}

function sourceTypeRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_SOURCE_TYPE_LIST.SUCCESS:
      return action.data
    default:
      return state
  }
}

function sourceTypeTagRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_SOURCE_TYPE_TAG.SUCCESS:
      return action.data
    default:
      return state
  }
}

function areaRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_AREA_LIST.SUCCESS:
      return _.zipObject(
        _.map(action.data, (area) => area.code),
        _.map(action.data, (area) => area)
      )

    default:
      return state
  }
}

function areaTree(state = [], action) {
  switch (action.type) {
    case ActionTypes.FETCH_AREA_LIST.SUCCESS: {
      const all = _.sortBy(action.data, ["code"])
      const china = _.filter(all, (area) => area.code < 990000)
      const world = _.filter(all, (area) => area.code > 990000)
      return [
        {
          code: -1,
          name: "国内",
          children: _.map(
            _.filter(china, (area) => area.code % 10000 === 0),
            (area) => {
              const result = {
                code: area.code,
                name: area.name,
                children: _.filter(china, (c) => c.code !== area.code && parseInt(c.code / 10000) === parseInt(area.code / 10000)),
              }
              // if (result.children.length === 1 && result.children[0].name.indexOf('未知') > -1) {
              //   result.children.splice(0, 0, {
              //     code: area.code,
              //     name: result.name,
              //   });
              // }
              return result
            }
          ),
        },
        {
          code: -2,
          name: "国外",
          children: world,
          // children: _.map(_.filter(world, area => area.code % 1000 === 0), area => {
          //   return {
          //     code: area.code,
          //     name: area.name,
          //     children: _.filter(world, c => c.code !== area.code && parseInt(c.code / 1000) === parseInt(area.code / 1000))
          //   };
          // })
        },
      ]
    }

    default:
      return state
  }
}

/** 权限
 {
  [privilegeId]: {...}
}
 */
// function privilegeRepo(state = {}, action) {
//   switch (action.type) {
//     case ActionTypes.SWITCH_AGENCY.SUCCESS:
//       return {};
//
//     case ActionTypes.FETCH_POSITION_PRIVILEGE_LIST.SUCCESS:
//       return _.assign({}, state, _.zipObject(
//         _.map(action.data, privilege => privilege.privilegeId),
//         action.data
//       ));
//
//     default:
//       return state;
//   }
// }

/** 权限
 {
   query, error, status， map
}
 */
function privilegeMap(state = {}, action) {
  switch (action.type) {
    case ActionTypes.SWITCH_AGENCY.SUCCESS:
      return {}

    case ActionTypes.FETCH_PRIVILEGE_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_PRIVILEGE_LIST.SUCCESS:
      return _.assign({}, state, {
        status: "SUCCESS",
        query: false,
        map: _.zipObject(
          _.map(action.data, (privilege) => privilege.id),
          action.data
        ),
      })

    case ActionTypes.FETCH_PRIVILEGE_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

function leadRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_LEAD_LIST.SUCCESS:
    case ActionTypes.FETCH_HIGHSEA_LEAD_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.items, (lead) => lead.leadId),
          action.data.items
        )
      )

    case ActionTypes.FETCH_LEAD.SUCCESS:
      return {
        ...state,
        [action.data.leadId]: {
          ...action.data,
          // 线索私海和商机私海详情编辑走到这里，
          // 编辑时由于没有线索标记 leadSigns 这个字段,
          // 会影响list的回显，这里先传空下 by fightingliu
          leadSigns: state[action.data?.leadId]?.leadSigns || [],
        },
      }

    default:
      return state
  }
}

function leadList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_LEAD_LIST.SUCCESS:
      return {
        items: _.map(action.data.items, (lead) => lead.leadId),
      }
    default:
      return state
  }
}

function outside(state = {}, action) {
  switch (action.type) {
    case ActionTypes.OUTSIDE_TRANSFER.SUCCESS:
      return {
        items: _.map(action.data.items, (lead) => lead.outsideTicketId),
      }
    default:
      return state
  }
}

function registration(state = {}, action) {
  switch (action.type) {
    case ActionTypes.REGISTRATION_TRANSFER.SUCCESS:
      return {
        items: _.map(action.data.items, (lead) => lead.registrationTicketId),
      }
    default:
      return state
  }
}

// 整理数据

function leadCustomizedFields(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_LEAD_CUSTOMIZED_FIELDS.SUCCESS:
      return {
        fixed: _.filter(action.data, (f) => f.fieldType === "DEFAULT"),
        basic: _.filter(action.data, (f) => f.fieldType === "BASIC"),
        action: _.filter(action.data, (f) => f.fieldType === "ACTION"),
        originData: action.data, // 后端按顺序 返回的数据
      }
    default:
      return state
  }
}

function potentialCustomizedFields(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_POTENTIAL_CUSTOMIZED_FIELDS.SUCCESS:
      return {
        fixed: _.filter(action.data, (f) => f.fieldType === "DEFAULT"),
        basic: _.filter(action.data, (f) => f.fieldType === "BASIC"),
        action: _.filter(action.data, (f) => f.fieldType === "ACTION"),
        outside: _.filter(action.data, (f) => f.fieldType === "OUTSIDE"),
        originData: action.data,
      }
    default:
      return state
  }
}

function orderCustomizedFields(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_ORDER_CUSTOMIZED_FIELDS.SUCCESS:
      return {
        fixed: _.filter(action.data, (f) => f.fieldType === "DEFAULT"),
        finance: _.filter(action.data, (f) => f.fieldType === "FINANCE"),
        basic: _.filter(action.data, (f) => f.fieldType === "BASIC"),
        action: _.filter(action.data, (f) => f.fieldType === "ACTION"),
        originData: action.data,
      }
    default:
      return state
  }
}

function conversionCustomizedViewFields(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_CONVERSION_CUSTOMIZED_FIELDS.SUCCESS:
      return {
        basic: _.filter(action.data, (f) => f.fieldType === "BASIC"),
        lead: _.filter(action.data, (f) => f.fieldType === "LEAD"),
        potential: _.filter(action.data, (f) => f.fieldType === "POTENTIAL"),
        order: _.filter(action.data, (f) => f.fieldType === "ORDER"),
      }
    default:
      return state
  }
}

function conversionRateCustomizedViewFields(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_CONVERSION_RATE_CUSTOMIZED_FIELDS.SUCCESS:
      return {
        basic: _.filter(action.data, (f) => f.fieldType === "BASIC"),
        lead: _.filter(action.data, (f) => f.fieldType === "LEAD"),
        potential: _.filter(action.data, (f) => f.fieldType === "POTENTIAL"),
        order: _.filter(action.data, (f) => f.fieldType === "ORDER"),
      }
    default:
      return state
  }
}

function efficiencyReportCustomizedViewFields(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_EFFICIENCY_CUSTOMIZED_FIELDS.SUCCESS:
      return {
        basic: _.filter(action.data, (f) => f.fieldType === "BASIC"),
        lead: _.filter(action.data, (f) => f.fieldType === "LEAD"),
        potential: _.filter(action.data, (f) => f.fieldType === "POTENTIAL"),
        order: _.filter(action.data, (f) => f.fieldType === "ORDER"),
      }
    default:
      return state
  }
}

function basicSearchList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_BASIC_QUERY_LIST.SUCCESS:
      return {
        items: _.map(action.data, (lead) => lead.leadId),
      }
    default:
      return state
  }
}

function basicSearchRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_BASIC_QUERY_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data, (lead) => lead.leadId),
          action.data
        )
      )

    default:
      return state
  }
}

function potentialRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_POTENTIAL_LIST.SUCCESS:
    case ActionTypes.FETCH_HIGHSEA_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.items, (lead) => lead.leadId),
          action.data.items
        )
      )

    case ActionTypes.FETCH_POTENTIAL.SUCCESS:
      return {
        ...state,
        [action.data.leadId]: _.assign(state[action.data.leadId], action.data),
      }

    default:
      return state
  }
}

function orderRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_ORDER_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.items, (lead) => lead.leadId),
          action.data.items
        )
      )

    case ActionTypes.FETCH_ORDER.SUCCESS:
      return {
        ...state,
        [action.data.leadId]: _.assign(state[action.data.leadId], action.data),
      }

    default:
      return state
  }
}

function outsideSearchParam(state = { outsideQueryType: "OUTSIDE_QUERY_TYPE_IN_PROCESS" }, action) {
  switch (action.type) {
    case ActionTypes.SET_QUERY_TYPE:
      return {
        ...state,
        outsideQueryType: action.queryType,
      }
    default:
      return state
  }
}

function outsideRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_OUTSIDE_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.items, (outsideTicket) => outsideTicket.outsideTicketId),
          action.data.items
        )
      )
    case ActionTypes.FETCH_OUTSIDE.SUCCESS:
      const { outsideTicketId } = action.data
      return _.assign({}, state, { [outsideTicketId]: action.data })
    default:
      return state
  }
}

function outsideList(state = {}, action) {
  if (action.type === ActionTypes.FETCH_OUTSIDE_LIST.SUCCESS) {
    return {
      items: _.map(action.data.items, (outsideTicket) => outsideTicket.outsideTicketId),
    }
  }
  return state
}

function outsideCount(state = { OUTSIDE_QUERY_TYPE_FOR_MY_APPROVAL: 0, OUTSIDE_QUERY_TYPE_IN_PROCESS: 0 }, action) {
  if (action.type === ActionTypes.FETCH_OUTSIDE_COUNT.SUCCESS) {
    return {
      ...state,
      OUTSIDE_QUERY_TYPE_FOR_MY_APPROVAL: action.data.OUTSIDE_QUERY_TYPE_FOR_MY_APPROVAL,
      OUTSIDE_QUERY_TYPE_IN_PROCESS: action.data.OUTSIDE_QUERY_TYPE_IN_PROCESS,
    }
  }
  return state
}

function potentialList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_POTENTIAL_LIST.SUCCESS:
      return {
        items: _.map(action.data.items, (lead) => lead.leadId),
      }

    default:
      return state
  }
}

// 获取orderList的代码
function orderList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_ORDER_LIST.SUCCESS:
      return {
        items: _.map(action.data.items, (lead) => lead.leadId),
      }

    default:
      return state
  }
}

function highSeaList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_HIGHSEA_LIST.SUCCESS:
    case ActionTypes.FETCH_HIGHSEA_LEAD_LIST.SUCCESS:
      return {
        items: _.map(action.data.items, (lead) => lead.leadId),
      }
    case ActionTypes.FETCH_HIGHSEA_LEAD_LIST.ERROR:
      return {
        items: [],
      }
    default:
      return state
  }
}

/** 坐席角色列表
 {
  query, error, status， list
}
 */
function seatRoles(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_SEAT_ROLE_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_SEAT_ROLE_LIST.SUCCESS:
      return _.assign({}, state, { status: "SUCCESS", query: false, list: action.data })

    case ActionTypes.FETCH_SEAT_ROLE_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

/** 坐席技能列表
 {
  [callCenterId]: { query, error, status， list }
}
 */
function skillGroups(state = {}, action) {
  const callCenterId = action.params ? action.params.callCenterId : null
  if (!callCenterId) {
    return state
  }

  switch (action.type) {
    case ActionTypes.FETCH_SEAT_SKILL_GROUP_LIST.QUERY:
      return _.assign({}, state, {
        [callCenterId]: _.defaults({ status: "QUERY", query: true }, state[callCenterId]),
      })

    case ActionTypes.FETCH_SEAT_SKILL_GROUP_LIST.SUCCESS:
      return _.assign({}, state, {
        [callCenterId]: { list: action.data },
      })

    case ActionTypes.FETCH_SEAT_SKILL_GROUP_LIST.ERROR:
      return _.assign({}, state, {
        [callCenterId]: _.defaults({ status: "ERROR", query: false, error: action.message }, state[callCenterId]),
      })

    default:
      return state
  }
}

function callCenter(state = {}, action) {
  switch (action.type) {
    case ActionTypes.CALL_CENTER_UPDATE:
      return {
        ...state,
        callCenter: action.callCenter,
        status: action.status,
        event: action.event,
      }

    default:
      return state
  }
}

function decryptedPhoneNumber(state = {}, action) {
  switch (action.type) {
    case ActionTypes.DIAL_OUT.SUCCESS:
      return {
        ...state,
        [action.payload.contactPhone]: action.data.contactPhone,
      }

    default:
      return state
  }
}

function customerRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_CUSTOMER.SUCCESS:
      return {
        ...state,
        [action.data.customerId]: action.data,
      }

    default:
      return state
  }
}

/**
 * 联系电话状态
 */
function contactPhoneStatusList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.CONTACT_PHONE_STATUS.SUCCESS:
      return {
        ...state,
        [action.data.phoneNumber]: action.data,
      }

    case ActionTypes.CONTACT_PHONE_LIST_STATUS.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data, (data) => data.phoneNumber),
          action.data
        )
      )

    default:
      return state
  }
}

/**
 * 业绩分析列表
 */
function performanceRepo(state = {}, action) {
  if (action.type === ActionTypes.FETCH_PERFORMANCE_LIST.SUCCESS) {
    return {
      items: action.data,
    }
  }
  return state
}

/**
 * 业务分析列表
 */
function businessAnalysisRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_CONVERSION_QUANTITY_LIST.SUCCESS:
      return {
        ...state,
        conversionItems: action.data,
      }
    case ActionTypes.FETCH_CONVERSION_RATE_QUANTITY_LIST.SUCCESS:
      return {
        ...state,
        conversionRateItems: action.data,
      }
    case ActionTypes.FETCH_EFFICIENCY_EVALUATION_LIST.SUCCESS:
      return {
        ...state,
        evaluationItems: action.data,
      }
    case ActionTypes.CACHE_BUSINESS_ANALYSIS_PARAMS:
      return {
        ...state,
        cachedParams: action.data,
      }
    default:
      return state
  }
}

/**
 * 话务分析列表
 */
function reportRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_REPORT_TRAFFIC_DETAIL_LIST.SUCCESS:
      return {
        ...state,
        details: action.data,
      }
    case ActionTypes.FETCH_REPORT_TRAFFIC_DAILY_LIST.SUCCESS:
      return {
        ...state,
        stats: action.data,
      }
    case ActionTypes.CLEAR_REPORT_TRAFFIC_DETAIL_LIST:
      return {
        ...state,
        details: null,
      }
    case ActionTypes.FETCH_REPORT_COST_LIST.SUCCESS:
      return {
        ...state,
        costs: {
          items: action.data.list,
        },
      }
    case ActionTypes.CACHE_REPORT_TRAFFIC_PARAMS:
      return {
        ...state,
        cachedParams: action.data,
      }
    case ActionTypes.FETCH_REPORT_CHARGE_LIST.SUCCESS:
      return {
        ...state,
        charges: {
          items: action.data.list,
        },
      }
    default:
      return state
  }
}

/**
 * 我的工作台
 */
function workbenchRepo(state = {}, action) {
  // 计算和
  const add = (value1, value2) => {
    if (typeof value1 !== "number" || typeof value2 !== "number") {
      if (typeof value1 !== "number") {
        return value2
      }
      if (typeof value2 !== "number") {
        return value1
      }
      return null
    }
    return value1 + value2
  }

  // 计算差
  const subtract = (value1, value2) => {
    if (typeof value1 !== "number" || typeof value2 !== "number") {
      if (typeof value1 !== "number") {
        return null
      }
      if (typeof value2 !== "number") {
        return value1
      }
      return null
    }
    return value1 - value2
  }

  // 计算商
  const devide = (value1, value2) => {
    if (typeof value1 !== "number" || typeof value2 !== "number" || value2 === 0) {
      return null
    }
    return value1 / value2
  }

  // 话务数据接口原始数据转换为页面原始数据
  const convertCallData = (children, employees) => {
    const convertedEmployees = employees.map((item, index) => {
      item.id = item.teamId
      item._type = DataType.EMPLOYEE
      // 提前计算好未达标人数
      item.employeeCountNoCompliant = subtract(item.employeeCount, item.employeeCountCompliant)
      return item
    })
    const convertedChildren = children.map((item, index) => {
      item.id = item.teamId
      item._type = DataType.TEAM
      // 提前计算好未达标人数
      item.employeeCountNoCompliant = subtract(item.employeeCount, item.employeeCountCompliant)
      return item
    })
    let items = [].concat(convertedEmployees).concat(convertedChildren)

    // 计算汇总行
    const amount = items.reduce(
      (total, currentValue) => ({
        employeeCount: add(total.employeeCount, currentValue.employeeCount),
        employeeCountCompliant: add(total.employeeCountCompliant, currentValue.employeeCountCompliant),
        employeeCountNoCompliant: add(total.employeeCountNoCompliant, currentValue.employeeCountNoCompliant),
        employeeCountCompliantByTimes: add(total.employeeCountCompliantByTimes, currentValue.employeeCountCompliantByTimes),
        employeeCountCompliantByDuration: add(total.employeeCountCompliantByDuration, currentValue.employeeCountCompliantByDuration),
        previousEmployeeCount: add(total.previousEmployeeCount, currentValue.previousEmployeeCount),
        previousEmployeeCountCompliant: add(total.previousEmployeeCountCompliant, currentValue.previousEmployeeCountCompliant),
        dialOutTimesTotal: add(total.dialOutTimesTotal, currentValue.dialOutTimesTotal),
        dialOutTimesConnected: add(total.dialOutTimesConnected, currentValue.dialOutTimesConnected),
        dialOutSecondsTalk: add(total.dialOutSecondsTalk, currentValue.dialOutSecondsTalk),
        employeeId: total.employeeId,
        teamId: total.teamId,
        _type: total._type,
      }),
      {
        employeeCount: null,
        employeeCountCompliant: null,
        employeeCountCompliantByTimes: null,
        employeeCountCompliantByDuration: null,
        previousEmployeeCount: null,
        previousEmployeeCountCompliant: null,
        dialOutTimesTotal: null,
        dialOutTimesConnected: null,
        dialOutSecondsTalk: null,
        teamId: null,
        _type: DataType.AMOUNT,
      }
    )

    // 计算平均值
    const average = {
      employeeCount: devide(amount.employeeCount, items.length),
      employeeCountCompliant: devide(amount.employeeCountCompliant, items.length),
      employeeCountNoCompliant: devide(amount.employeeCountNoCompliant, items.length),
      employeeCountCompliantByTimes: devide(amount.employeeCountCompliantByTimes, items.length),
      employeeCountCompliantByDuration: devide(amount.employeeCountCompliantByDuration, items.length),
      employeeId: null,
      previousEmployeeCount: devide(amount.previousEmployeeCount, items.length),
      previousEmployeeCountCompliant: devide(amount.previousEmployeeCountCompliant, items.length),
      dialOutTimesTotal: devide(amount.dialOutTimesTotal, items.length),
      dialOutTimesConnected: devide(amount.dialOutTimesConnected, items.length),
      dialOutSecondsTalk: devide(amount.dialOutSecondsTalk, items.length),
      teamId: null,
      _type: DataType.AVERAGE,
    }

    // 暂不上线平均值、汇总
    items = [].concat(items) // .concat(average).concat(amount)

    // 数据增加层级信息
    items.forEach((item) => {
      item._level = action.payload._level
    })

    return items
  }

  // 销售数据接口原始数据转换为页面原始数据
  const convertSaleData = (children, employees) => {
    const convertedEmployees = employees.map((item, index) => {
      item.id = item.teamId
      item._type = DataType.EMPLOYEE
      return item
    })
    const convertedChildren = children.map((item, index) => {
      item.id = item.teamId
      item._type = DataType.TEAM
      return item
    })
    let items = [].concat(convertedEmployees).concat(convertedChildren)

    // 计算汇总行
    const amount = items.reduce(
      (total, currentValue) => ({
        employeeCount: add(total.employeeCount, currentValue.employeeCount),
        leadCountCreated: add(total.leadCountCreated, currentValue.leadCountCreated),
        orderAmountRecharged: add(total.orderAmountRecharged, currentValue.orderAmountRecharged),
        orderAmountServiceFee: add(total.orderAmountServiceFee, currentValue.orderAmountServiceFee),
        orderAmountTotal: add(total.orderAmountTotal, currentValue.orderAmountTotal),
        orderCountPaid: add(total.orderCountPaid, currentValue.orderCountPaid),
        potentialCountCreated: add(total.potentialCountCreated, currentValue.potentialCountCreated),
        previousOrderAmountRecharged: add(total.previousOrderAmountRecharged, currentValue.previousOrderAmountRecharged),
        previousOrderAmountServiceFee: add(total.previousOrderAmountServiceFee, currentValue.previousOrderAmountServiceFee),
        previousOrderAmountTotal: add(total.previousOrderAmountTotal, currentValue.previousOrderAmountTotal),
        previousOrderCountPaid: add(total.previousOrderCountPaid, currentValue.previousOrderCountPaid),
        employeeId: total.employeeId,
        teamId: total.teamId,
        _type: total._type,
      }),
      {
        employeeCount: null,
        employeeId: null,
        leadCountCreated: null,
        orderAmountRecharged: null,
        orderAmountServiceFee: null,
        orderAmountTotal: null,
        orderCountPaid: null,
        potentialCountCreated: null,
        previousOrderAmountRecharged: null,
        previousOrderAmountServiceFee: null,
        previousOrderAmountTotal: null,
        previousOrderCountPaid: null,
        teamId: null,
        _type: DataType.AMOUNT,
      }
    )

    // 计算平均值
    const average = {
      employeeCount: devide(amount.employeeCount, items.length),
      employeeId: null,
      leadCountCreated: devide(amount.leadCountCreated, items.length),
      orderAmountRecharged: devide(amount.orderAmountRecharged, items.length),
      orderAmountServiceFee: devide(amount.orderAmountServiceFee, items.length),
      orderAmountTotal: devide(amount.orderAmountTotal, items.length),
      orderCountPaid: devide(amount.orderCountPaid, items.length),
      potentialCountCreated: devide(amount.potentialCountCreated, items.length),
      previousOrderAmountRecharged: devide(amount.previousOrderAmountRecharged, items.length),
      previousOrderAmountServiceFee: devide(amount.previousOrderAmountServiceFee, items.length),
      previousOrderAmountTotal: devide(amount.previousOrderAmountTotal, items.length),
      previousOrderCountPaid: devide(amount.previousOrderCountPaid, items.length),
      teamId: null,
      _type: DataType.AVERAGE,
    }

    // 暂不上线平均值、汇总
    items = [].concat(items) // .concat(average).concat(amount)

    // 数据增加层级信息
    items.forEach((item) => {
      item._level = action.payload._level
    })

    return items
  }

  switch (action.type) {
    case ActionTypes.FETCH_ROOT_TEAM_SALE_DATA_LIST.SUCCESS:
      return {
        ...state,
        saleDataRoot: convertSaleData(action.data.children, action.data.employees),
        saleDataMap: {}, // 更新root时要清除其它子数据
      }
    case ActionTypes.FETCH_TEAM_SALE_DATA_LIST.SUCCESS:
      const saleDataMap = state.saleDataMap || {}
      saleDataMap[action.payload.teamId] = convertSaleData(action.data.children, action.data.employees)
      return {
        ...state,
        saleDataMap: _.cloneDeep(saleDataMap),
      }
    case ActionTypes.FETCH_TEAM_SALE_DATA_CHART.SUCCESS:
      return {
        ...state,
        saleDataChart: convertSaleData(action.data.children, action.data.employees).filter((item) => item._type === DataType.TEAM || item._type === DataType.EMPLOYEE),
      }
    // 个人工作台话务报表
    case ActionTypes.FETCH_ROOT_TEAM_CALL_DATA_LIST.SUCCESS:
      return {
        ...state,
        callDataRoot: convertCallData(action.data.children, action.data.employees),
        callDataMap: {}, // 更新root时要清除其它子数据
      }
    case ActionTypes.FETCH_TEAM_CALL_DATA_LIST.SUCCESS:
      const callDataMap = state.callDataMap || {}
      callDataMap[action.payload.teamId] = convertCallData(action.data.children, action.data.employees)
      return {
        ...state,
        callDataMap: _.cloneDeep(callDataMap),
      }
    default:
      return state
  }
}

function accountAssignRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.SEARCH_UNASSIGNED_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.items, (item) => item.registrationTicketId),
          action.data.items
        )
      )
    default:
      return state
  }
}

function accountAssignList(state = {}, action) {
  if (action.type === ActionTypes.SEARCH_UNASSIGNED_LIST.SUCCESS) {
    return {
      items: _.map(action.data.items, (item) => item.registrationTicketId),
    }
  }
  return state
}

function accountRegisterRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.SEARCH_REGISTER_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data.items, (item) => item.registrationTicketId),
          action.data.items
        )
      )

    default:
      return state
  }
}

function accountRegisterList(state = {}, action) {
  if (action.type === ActionTypes.SEARCH_REGISTER_LIST.SUCCESS) {
    return {
      items: _.map(action.data.items, (item) => item.registrationTicketId),
    }
  }
  return state
}

function registerOperatorRepo(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_OPERATOR_LIST.SUCCESS:
      return _.assign(
        {},
        state,
        _.zipObject(
          _.map(action.data, (registerOperator) => registerOperator.userId),
          _.map(action.data, (registerOperator) => ({
            label: registerOperator.name,
            value: registerOperator.userId,
          }))
        )
      )
    default:
      return state
  }
}

function registerOperatorList(state = { items: [] }, action) {
  switch (action.type) {
    case ActionTypes.FETCH_OPERATOR_LIST.SUCCESS:
      return {
        items: _.map(action.data, (registerOperator) => registerOperator.userId),
      }
    default:
      return state
  }
}

function assignProcessorList(state = { item: [] }, action) {
  switch (action.type) {
    case ActionTypes.FETCH_ASSIGN_PROCESSOR_LIST.SUCCESS:
      return {
        items: _.map(action.data, (item) => {
          return {
            label: item.name,
            value: item.userId,
          }
        }),
      }
    default:
      return state
  }
}

/** -------------- portal-fe 员工管理 -------------------------- */

/** 可管理的服务商列表
 {
  query, error, status， list
}
 */
function managedAgencyList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_MANAGED_AGENCY_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_MANAGED_AGENCY_LIST.SUCCESS:
      return _.assign({}, state, { status: "SUCCESS", query: false, list: action.data })

    case ActionTypes.FETCH_MANAGED_AGENCY_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

/** 头衔列表
 {
  query, error, status， list
}
 */
function titleList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_TITLE_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_TITLE_LIST.SUCCESS:
      return _.assign({}, state, { status: "SUCCESS", query: false, list: action.data })

    case ActionTypes.FETCH_TITLE_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

/** 职位列表
 {
  query, error, status， list
}
 */
function positionList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_POSITION_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_POSITION_LIST.SUCCESS:
      return _.assign({}, state, { status: "SUCCESS", query: false, list: action.data })

    case ActionTypes.FETCH_POSITION_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

/** 角色列表
 {
  query, error, status， list
}
 */
function roleList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_ROLE_LIST.QUERY:
      return _.assign({}, state, { status: "QUERY", query: true })

    case ActionTypes.FETCH_ROLE_LIST.SUCCESS:
      return _.assign({}, state, { status: "SUCCESS", query: false, list: action.data })

    case ActionTypes.FETCH_ROLE_LIST.ERROR:
      return _.assign({}, state, { status: "ERROR", query: false, error: action.message })

    default:
      return state
  }
}

/** -------------- portal-fe-end 员工管理 -------------------------- */

/**
 * @description: 清洗线索原因配置
 */
function cleanReasonConfig(state = {}, action) {
  // 排序处理
  const handleReasonTagSort = (item) => {
    const data = _.map(
      _.sortBy(item, v => v.index), (v, i) => {
        const _item = {
          desc: v.cleaningReason,
          parent_id: v.parentLeadStateReasonTagId,
          value: v.leadStateReasonTagId,
        }
        if (v?.childrenReasonTags?.length > 0) {
          _item.options = handleReasonTagSort(v.childrenReasonTags)
        }
        return _item
    })
    return data;
  }
  const handleConfig = (data) => {
    const { leadStateReasonTags, isOpenStateTag = false } = data
    const reasonList = {}
    const _reasonList = {}
    CLEANING_STATUS.map((v) => {
      if (!v.filter) {
        return (reasonList[v.value] = handleReasonTagSort(
          _.filter(leadStateReasonTags, d => d.cleaningStatus === v.value)
        ))
      } else {
        return (_reasonList[v.value] = handleReasonTagSort(
          _.filter(leadStateReasonTags, d => d.cleaningStatus === v.value)
        ))
      }
    })
    return { isOpenStateTag, reasonList, reasonFilterList: {...reasonList, ..._reasonList}}
  }
  switch (action.type) {
    case ActionTypes.FETCH_CLEAN_REASON_CONFIG.SUCCESS:
      const { data = {} } = action
      if (data.code === 0) {
        const _data = handleConfig(data)
        return _.assign({}, state, _data)
      }
    default:
      return state
  }
}

/**
 * @description: 呼入白名单
 */
function customerProtectionWhiteList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_CUSTOMER_PROTECTION_WHITE_LIST.SUCCESS:
      return _.assign({}, state, {
        data: action.data
      })
    default:
      return state
  }
}

/**
 * @description: 总机号列表
 */
function listDisplayNumber(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_LIST_DISPLAY_NUMBER.SUCCESS:
      return _.assign({}, state, {
        data: action.data
      })
    default:
      return state
  }
}

function weChatLabelList(state = {}, action) {
  switch (action.type) {
    case ActionTypes.FETCH_WECHAT_LABEL_LIST.SUCCESS:
      return _.assign({}, state, {
        ...action.data
      })
    default:
      return state
  }
}

// 返回 reducer
export default combineReducers({
  loginStatus,
  userRepo,
  currentUser,
  agencyRepo,
  agencyList,
  agencyListPage,
  channelManagerRepo,
  channelManagerList,
  employeeRepo,
  employeeList,
  industryRepo,
  industryList,
  industryCreate,
  promotedObjectRepo,
  sourceTypeRepo,
  sourceTypeTagRepo,
  areaRepo,
  areaTree,
  potentialRepo,
  potentialList,
  orderRepo,
  orderList,
  highSeaList,
  agencyPreferenceRepo, // 代理商首选项
  privilegeMap, // 权限
  leadRepo,
  leadList,
  outside,
  registration,
  basicSearchRepo,
  basicSearchList,
  seatRoles,
  skillGroups,
  callCenter,
  decryptedPhoneNumber,
  customerRepo,
  leadCustomizedFields,
  potentialCustomizedFields,
  orderCustomizedFields,
  conversionCustomizedViewFields,
  conversionRateCustomizedViewFields,
  efficiencyReportCustomizedViewFields,
  contactPhoneStatusList,
  reportRepo,
  outsideSearchParam, // 外勤单列表的查询参数
  outsideRepo,
  outsideList,
  performanceRepo,
  businessAnalysisRepo,
  outsideCount,
  organizationTree, // 架构树
  teamRepo, // 团队信息
  workbenchRepo, // 我的工作台

  // register 开户
  accountAssignRepo,
  accountAssignList,
  accountRegisterRepo,
  accountRegisterList,
  registerOperatorRepo,
  registerOperatorList,
  assignProcessorList,

  // 员工管理
  managedAgencyList,
  titleList,
  positionList,
  roleList,

  fullEmployeeRepo,
  fullEmployeeList,
  cleanReasonConfig,
  customerProtectionWhiteList,
  listDisplayNumber,
  weChatLabelList,
})
